import { Category } from "@common/types/category";
import { createTransactionsUrl } from "@components/transactions/lib/transactionsUrl";
import { endOfDay, parseISO } from "date-fns";
import { useCallback, useContext, useMemo } from "react";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { COUNTERPARTY_DRILL_DOWN_AVAILABLE_FILTERS } from "@components/transactions/views/DefaultCounterpartyDrillDownTransactionView";
import { useClasses } from "@api/class.api";
import { DirectoryContext } from "@components/settings/Directory/Directory.context";
import { TransactionsFilters } from "@components/transactions/filters/lib";
import { useEntities } from "./useEntities";

export function useCounterpartyDrillDownAvailableFilters(): FilterTabs[] {
    const { isActivated: isClassesEnabled } = useClasses();
    return useMemo(
        () => [
            ...COUNTERPARTY_DRILL_DOWN_AVAILABLE_FILTERS,
            ...(isClassesEnabled ? [FilterTabs.CLASS] : []),
        ],
        [isClassesEnabled],
    );
}

interface Options {
    includeDateFilter: boolean;
    categories?: Category[];
}

export function useCounterpartyTransactionsFilters(
    counterpartyId: string | null,
    { includeDateFilter, categories }: Options,
) {
    const { filters } = useContext(DirectoryContext);
    const entities = useEntities();

    return useMemo(() => {
        const transactionFilters: TransactionsFilters = {
            counterpartiesIds: counterpartyId ? [counterpartyId] : undefined,
            withEmptyOrUnverifiedCounterparty: !counterpartyId
                ? true
                : undefined,
            category: categories?.map((c) => c.id),
            entityIds: filters.entityIds?.length
                ? filters.entityIds
                : entities.map((e) => e.id),
        };

        if (includeDateFilter && filters.startDate) {
            transactionFilters.start = parseISO(filters.startDate);
            transactionFilters.end = filters.endDate
                ? endOfDay(parseISO(filters.endDate))
                : undefined;
        }

        return transactionFilters;
    }, [
        counterpartyId,
        filters.startDate,
        filters.endDate,
        includeDateFilter,
        categories,
        filters.entityIds,
        entities,
    ]);
}

export function useCounterpartyTransactionsOpener(
    counterpartyId: string | null,
    options: Options,
) {
    const filters = useCounterpartyTransactionsFilters(counterpartyId, options);
    const link = useMemo(() => createTransactionsUrl(filters), [filters]);

    return useCallback(() => {
        window.open(link);
    }, [link]);
}
